// src/utils/api.js
import { Storage } from '../constants/storage';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
const AUTH_BASE_URL = import.meta.env.VITE_AUTH_BASE_URL || 'https://api2.simplize.vn';
const AUTH_LOGIN_PATH = import.meta.env.VITE_AUTH_LOGIN_PATH || '/api/general/auth/login';
const AUTH_SOCIAL_LOGIN_PATH = import.meta.env.VITE_AUTH_SOCIAL_LOGIN_PATH || '/api/general/auth/social/login';
const AUTH_REFRESH_PATH = import.meta.env.VITE_AUTH_REFRESH_PATH || '/api/general/auth/refresh-token';
const USER_CURRENT_PATH = import.meta.env.VITE_USER_CURRENT_PATH || '/api/general/user/current';
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '143577374442-po4dnm24i6193m84htcqimp7g4gf45ov.apps.googleusercontent.com';
const FACEBOOK_APP_ID = import.meta.env.VITE_FACEBOOK_APP_ID || '';
const APPLE_CLIENT_ID = import.meta.env.VITE_APPLE_CLIENT_ID || '';
const APPLE_REDIRECT_URI = import.meta.env.VITE_APPLE_REDIRECT_URI || `${window.location.origin}/login`;
const APPLE_CLIENT = import.meta.env.VITE_APPLE_CLIENT || '';
const AUTH_REGISTER_PATH = import.meta.env.VITE_AUTH_REGISTER_PATH || '/api/general/auth/register';
const AUTH_FORGOT_PASSWORD_PATH = import.meta.env.VITE_AUTH_FORGOT_PASSWORD_PATH || '/api/general/auth/reset-password/init';
const AUTH_EMAIL_SEND_OTP_PATH = import.meta.env.VITE_AUTH_EMAIL_SEND_OTP_PATH || '/api/general/auth/email/send-otp';
const AUTH_EMAIL_VERIFY_OTP_PATH = import.meta.env.VITE_AUTH_EMAIL_VERIFY_OTP_PATH || '/api/general/auth/email/verify-otp';
const AUTH_EMAIL_RESEND_OTP_PATH = import.meta.env.VITE_AUTH_EMAIL_RESEND_OTP_PATH || '/api/general/auth/email/resend-otp';
const AUTH_VERIFY_OTP_PATH = import.meta.env.VITE_AUTH_VERIFY_OTP_PATH || '/api/general/auth/register/verify-otp';
const AUTH_RESEND_OTP_PATH = import.meta.env.VITE_AUTH_RESEND_OTP_PATH || '/api/general/auth/otp/resend';

const AUTH_STORAGE_KEY = Storage.auth;

export const authStorage = {
    get: () => {
        try {
            const raw = localStorage.getItem(AUTH_STORAGE_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    },
    set: (auth) => {
        try {
            localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(auth));
            return true;
        } catch {
            return false;
        }
    },
    remove: () => {
        try {
            localStorage.removeItem(AUTH_STORAGE_KEY);
            localStorage.removeItem(Storage.access_token);
            localStorage.removeItem(Storage.refresh_token);
            localStorage.removeItem(Storage.expired_time);
            return true;
        } catch {
            return false;
        }
    }
};

export const getAccessToken = () => {
    const fromObject = authStorage.get()?.accessToken;
    if (fromObject) return fromObject;
    try {
        return localStorage.getItem(Storage.access_token) || null;
    } catch {
        return null;
    }
};
export const getRefreshToken = () => {
    const fromObject = authStorage.get()?.refreshToken;
    if (fromObject) return fromObject;
    try {
        return localStorage.getItem(Storage.refresh_token) || null;
    } catch {
        return null;
    }
};
export const getExpiredTime = () => {
    const fromObject = authStorage.get()?.expiredTime;
    if (fromObject) return fromObject;
    try {
        const v = localStorage.getItem(Storage.expired_time);
        return v ? Number(v) : 0;
    } catch {
        return 0;
    }
};
export const isAuthenticated = () => {
    // UI gating: only require token presence. Expiry will be handled by refresh flow.
    return Boolean(getAccessToken());
};

let ongoingRefreshPromise = null;

export const refreshAccessToken = async () => {
    if (ongoingRefreshPromise) return ongoingRefreshPromise;
    const refreshToken = getRefreshToken();
    if (!refreshToken) throw new Error('No refresh token');

    ongoingRefreshPromise = (async () => {
        const url = `${AUTH_BASE_URL}${AUTH_REFRESH_PATH}`;
        const resp = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ refreshToken })
        });

        if (!resp.ok) {
            authStorage.remove();
            throw new Error(`Refresh failed: ${resp.status}`);
        }

        const json = await resp.json();
        const data = json?.data || json;
        if (!data?.accessToken) {
            authStorage.remove();
            throw new Error('Invalid refresh response');
        }

        const newAuth = {
            accessToken: data.accessToken,
            refreshToken: data.refreshToken || refreshToken,
            expiredTime: data.expiredTime || (Date.now() + 15 * 60_000),
            isExisted: data.isExisted
        };
        authStorage.set(newAuth);
        try {
            localStorage.setItem(Storage.access_token, newAuth.accessToken);
            localStorage.setItem(Storage.refresh_token, newAuth.refreshToken);
            localStorage.setItem(Storage.expired_time, String(newAuth.expiredTime));
        } catch {}
        return newAuth.accessToken;
    })();

    try {
        return await ongoingRefreshPromise;
    } finally {
        ongoingRefreshPromise = null;
    }
};

export const getUserId = () => {
    // 1) Prefer uid from JWT when authenticated
    const token = getAccessToken();
    if (token) {
        try {
            const payload = decodeJwtPayload(token);
            // Do NOT use email; prefer uid or id-like fields only
            const authId = payload?.uid || payload?.id || payload?.sub || payload?.uuid;
            if (authId !== undefined && authId !== null) return String(authId);
        } catch {}
    }
    // 2) Fallback: id from cached current user
    try {
        const raw = localStorage.getItem(Storage.current_user);
        if (raw) {
            const cached = JSON.parse(raw);
            if (cached && (cached.id !== undefined && cached.id !== null)) {
                return String(cached.id);
            }
        }
    } catch {}
    // 3) Final fallback: generated anonymous id
    let userId = localStorage.getItem(Storage.super_agent_user_id);
    if (!userId) {
        userId = 'user_' + Math.random().toString(36).substring(2, 15);
        localStorage.setItem(Storage.super_agent_user_id, userId);
    }
    return userId;
};

const parseServerMessage = (response) => {
    if (!response) return null;
    if (typeof response === 'object') return response.message || response.error || null;
    if (typeof response !== 'string') return null;
    try {
        const parsed = JSON.parse(response);
        return parsed?.message || parsed?.error || null;
    } catch {
        return response.trim() || null;
    }
};

// API Error handler
class APIError extends Error {
    constructor(message, status, response) {
        super(parseServerMessage(response) || message);
        this.name = 'APIError';
        this.status = status;
        this.response = response;
    }
}

export const getApiErrorMessage = (err, fallback = 'Đã có lỗi xảy ra. Vui lòng thử lại sau.') =>
    parseServerMessage(err?.response) ||
    parseServerMessage(err?.data) ||
    (typeof err?.error === 'string' ? err.error : null) ||
    (err?.message && !/^API Error:/.test(err.message) ? err.message : null) ||
    fallback;

// Base fetch wrapper with error handling
const fetchWithErrorHandling = async (url, options = {}) => {
    try {
        const mergedOptions = {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...(options.headers || {}),
            },
        };
        // Auto-serialize plain objects to JSON
        const hasBody = Object.prototype.hasOwnProperty.call(mergedOptions, 'body');
        if (hasBody) {
            const body = mergedOptions.body;
            const isPlainObject = body && typeof body === 'object' && !(body instanceof FormData) && !(body instanceof Blob) && !(body instanceof URLSearchParams);
            if (isPlainObject) {
                mergedOptions.body = JSON.stringify(body);
            }
            // Remove JSON content-type for FormData/Blob
            if (body instanceof FormData || body instanceof Blob) {
                delete mergedOptions.headers['Content-Type'];
            }
        }
        const response = await fetch(url, mergedOptions);

        if (!response.ok) {
            const errorText = await response.text();
            let parsedBody = null;
            try {
                parsedBody = errorText ? JSON.parse(errorText) : null;
            } catch {
                parsedBody = null;
            }
            const bodyStatus = parsedBody?.status;
            if (parsedBody && typeof bodyStatus === 'number' && bodyStatus < 400) {
                return parsedBody;
            }
            throw new APIError(
                `API Error: ${response.status} ${response.statusText}`,
                response.status,
                errorText
            );
        }

        // Check if response has content to parse
        const contentType = response.headers.get('content-type');
        const contentLength = response.headers.get('content-length');
        
        // If no content or content-length is 0, return null (successful but empty response)
        if (contentLength === '0' || response.status === 204) {
            return null;
        }
        
        // Check if response is JSON
        if (contentType && contentType.includes('application/json')) {
            // Check if response body is not empty before parsing
            const text = await response.text();
            if (text.trim() === '') {
                return null;
            }
            return JSON.parse(text);
        }

        return response;
    } catch (error) {
        if (error instanceof APIError) {
            throw error;
        }

        const friendlyMessage = error.message === 'Failed to fetch'
            ? 'Không thể kết nối với máy chủ'
            : error.message || 'Đã xảy ra lỗi';

        throw new APIError(
            friendlyMessage,
            0,
            null
        );
    }
};

function getDefaultHeaders(extra = {}) {
    const headers = { 'X-App-Code': 'nebula', ...extra };
    const token = getAccessToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
}

// Route the browser to /login when we have definitively lost the session.
// Keeps the current URL as a redirect target so the login flow can bring
// the user back after signing in.
function redirectToLogin() {
    if (typeof window === 'undefined') return;
    const { pathname, search } = window.location;
    if (pathname === '/login') return;
    if (pathname === '/') return;
    const returnUrl = encodeURIComponent(`${pathname}${search || ''}`);
    window.location.assign(`/login?returnUrl=${returnUrl}`);
}

// Wrapper: attach Authorization + auto refresh on 401/expired
export const authorizedFetch = async (url, options = {}) => {
    if (!getAccessToken() && !getRefreshToken()) {
        redirectToLogin();
        throw new APIError('Unauthorized', 401, null);
    }
    const attachAuthHeader = async () => {
        let token = getAccessToken();
        const expiredTime = getExpiredTime();
        const isExpired = !token || (expiredTime && Date.now() >= (Number(expiredTime) - 30_000));
        if (isExpired && getRefreshToken()) {
            try {
                token = await refreshAccessToken();
            } catch (e) {
                // fallthrough; will likely 401
            }
        }
        const headers = getDefaultHeaders(options.headers || {});
        if (token) headers['Authorization'] = `Bearer ${token}`;
        return { ...options, headers };
    };

    // Build full URL if relative path provided
    const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
    
    let attemptOptions = await attachAuthHeader();
    try {
        return await fetchWithErrorHandling(fullUrl, attemptOptions);
    } catch (err) {
        if (err instanceof APIError && err.status === 401 && getRefreshToken()) {
            try {
                const newToken = await refreshAccessToken();
                const retryOptions = {
                    ...options,
                    headers: getDefaultHeaders({
                        ...(options.headers || {}),
                        Authorization: `Bearer ${newToken}`,
                    }),
                };
                return await fetchWithErrorHandling(fullUrl, retryOptions);
            } catch (e) {
                authStorage.remove();
                redirectToLogin();
                throw err;
            }
        }
        throw err;
    }
};

export const noAuthorizedFetch = async (url, options = {}) => {
    const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
    const headers = getDefaultHeaders(options.headers || {});
    return fetchWithErrorHandling(fullUrl, { ...options, headers });
};

export const apiClient = {
    // Fetch all sessions for a user
    async fetchSessions(userId) {
        if (!userId) {
            throw new Error("User ID is required to fetch sessions.");
        }
        const url = `${API_BASE_URL}/users/${userId}/sessions`;
        return authorizedFetch(url);
    },

    // Create new session
    async createSession(userId) {
        const url = `${API_BASE_URL}/users/${userId}/sessions`;
        return authorizedFetch(url, {
            method: 'POST',
        });
    },

    // Send message with streaming response
    async sendMessage({ sessionId, shareId, message, mode = null, contentUrls = [], isAutoSuggest = false, greetingContext = null }) {
        const requestBody = {
            message: message
        };

        if (sessionId) {
            requestBody.session_id = sessionId;
        }
        if (shareId) {
            requestBody.share_id = shareId;
        }
        if (mode) {
            requestBody.mode = mode;
        }
        if (contentUrls && contentUrls.length > 0) {
            requestBody.content_urls = contentUrls;
        }

        if (isAutoSuggest) {
            requestBody.prompt_source = 'autosuggest';
            requestBody.query_source = 'autosuggest';
            requestBody.search_focus = 'internet';
        } else if (greetingContext) {
            requestBody.prompt_source = 'greeting';
            requestBody.query_source = greetingContext;
            requestBody.search_focus = 'combined';
        }

        let headers = getDefaultHeaders({ 'Content-Type': 'application/json' });

        let response = await fetch(`${API_BASE_URL}/chat/stream`, {
            method: 'POST',
            body: JSON.stringify(requestBody),
            headers,
        });

        if (!response.ok) {
            if (response.status === 401 && getRefreshToken()) {
                try {
                    const newToken = await refreshAccessToken();
                    response = await fetch(`${API_BASE_URL}/chat/stream`, {
                        method: 'POST',
                        body: JSON.stringify(requestBody),
                        headers: getDefaultHeaders({
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${newToken}`,
                        }),
                    });
                } catch {}
            }
        }

        if (!response.ok) {
            // Parse error response body for better error handling (especially 429)
            let errorBody = null;
            try {
                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('application/json')) {
                    errorBody = await response.json();
                } else {
                    errorBody = await response.text();
                }
            } catch (e) {
                console.error('Failed to parse error response:', e);
            }

            throw new APIError(
                `HTTP error! status: ${response.status}`,
                response.status,
                errorBody || response
            );
        }

        return response;
    },

    // =====================================================
    // V2 API Methods (Email Support Agent)
    // =====================================================

    // Send message with V2 streaming (HITL support)
    async sendMessageV2({ sessionId, message, senderEmail, model, contentUrls = [] }) {
        const requestBody = {
            message: message,
            sender_email: senderEmail || 'user@example.com',
        };
        if (sessionId) {
            requestBody.session_id = sessionId;
        }
        if (model) {
            requestBody.model = model;
        }
        if (contentUrls && contentUrls.length > 0) {
            requestBody.content_urls = contentUrls;
        }

        let headers = getDefaultHeaders({ 'Content-Type': 'application/json' });

        let response = await fetch(`${API_BASE_URL}/v2/chat/stream`, {
            method: 'POST',
            body: JSON.stringify(requestBody),
            headers,
        });

        if (!response.ok) {
            if (response.status === 401 && getRefreshToken()) {
                try {
                    const newToken = await refreshAccessToken();
                    response = await fetch(`${API_BASE_URL}/v2/chat/stream`, {
                        method: 'POST',
                        body: JSON.stringify(requestBody),
                        headers: getDefaultHeaders({
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${newToken}`,
                        }),
                    });
                } catch {}
            }
        }

        if (!response.ok) {
            let errorBody = null;
            try {
                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('application/json')) {
                    errorBody = await response.json();
                } else {
                    errorBody = await response.text();
                }
            } catch (e) {
                console.error('Failed to parse error response:', e);
            }

            throw new APIError(
                `HTTP error! status: ${response.status}`,
                response.status,
                errorBody || response
            );
        }

        return response;
    },

    // Submit approval for HITL (V2)
    async submitApproval({ sessionId, isApproved, userEditContent, answers }) {
        const url = `${API_BASE_URL}/v2/approval`;
        return authorizedFetch(url, {
            method: 'POST',
            body: {
                session_id: sessionId,
                is_approved: isApproved,
                user_edit_content: userEditContent,
                answers: answers,
            },
        });
    },

    // Push message to active streaming session (V2)
    // Allows sending additional messages while agent is still processing
    async pushMessage({ sessionId, message }) {
        const url = `${API_BASE_URL}/v2/push-message`;
        return authorizedFetch(url, {
            method: 'POST',
            body: {
                session_id: sessionId,
                message: message,
            },
        });
    },

    // Get session state (V2)
    async getSessionStateV2(sessionId) {
        const url = `${API_BASE_URL}/v2/sessions/${sessionId}`;
        return authorizedFetch(url);
    },

    // Fetch session history (V2) - Returns role-based messages with content blocks
    async fetchHistoryV2(sessionId) {
        const url = `${API_BASE_URL}/v2/sessions/${sessionId}/history`;
        return authorizedFetch(url);
    },

    // Rename session
    async renameSession(sessionId, userId, newName) {
        const url = `${API_BASE_URL}/sessions/${sessionId}`;
        return authorizedFetch(url, {
            method: 'PATCH',
            body: {
                user_id: userId,
                new_name: newName.trim()
            },
        });
    },

    // Delete session
    async deleteSession(sessionId, userId) {
        const url = `${API_BASE_URL}/sessions/${sessionId}`;
        return authorizedFetch(url, {
            method: 'DELETE',
            body: { user_id: userId },
        });
    },

    // Health check endpoint
    async healthCheck() {
        try {
            const response = await fetch(`${API_BASE_URL}/health`);
            return response.ok;
        } catch {
            return false;
        }
    },

    // Get API status and configuration
    async getApiStatus() {
        try {
            return await fetchWithErrorHandling(`${API_BASE_URL}/status`);
        } catch (error) {
            console.warn('API status check failed:', error);
            return {
                status: 'unknown',
                version: 'unknown',
                uptime: 0
            };
        }
    },

    async getAvailableModels() {
        try {
            const response = await fetch(`${API_BASE_URL}/v2/models`);
            if (!response.ok) return { models: [] };
            return await response.json();
        } catch {
            return { models: [] };
        }
    },

    // Get suggestions
    async getSuggestions() {
        // This is a public endpoint, so we use fetchWithErrorHandling directly
        const url = `${API_BASE_URL}/suggestions`;
        try {
            const suggestions = await fetchWithErrorHandling(url);
            // API returns an array of strings, let's convert to object for consistency
            if (Array.isArray(suggestions)) {
                return suggestions.map(s => (typeof s === 'string' ? { content: s, type: 'question' } : s ));
            }
            return [];
        } catch (error) {
            console.warn("Could not fetch suggestions:", error);
            return []; // Return empty array on error
        }
    },

    // Get a pre-signed URL for file uploads
    async getUploadUrl(fileName, fileType, fileSize, contentHash = null) {
        const url = `${API_BASE_URL}/api/files/upload-url`;
        const body = {
            file_name: fileName,
            file_type: fileType,
            file_size: fileSize
        };

        // Include content hash if provided (for deduplication)
        if (contentHash) {
            body.content_hash = contentHash;
        }

        return authorizedFetch(url, {
            method: 'POST',
            body,
        });
    },

    // =====================================================
    // V2 File Upload API Methods
    // =====================================================

    // Get a pre-signed URL for file uploads (V2)
    async getUploadUrlV2(fileName, fileType, fileSize, contentHash = null) {
        const url = `${API_BASE_URL}/v2/files/upload-url`;
        const body = {
            file_name: fileName,
            file_type: fileType,
            file_size: fileSize
        };

        if (contentHash) {
            body.content_hash = contentHash;
        }

        return authorizedFetch(url, {
            method: 'POST',
            body,
        });
    },

    // Get presigned download URL (V2)
    async getDownloadUrlV2(contentUrl) {
        const url = `${API_BASE_URL}/v2/files/download-url?content_url=${encodeURIComponent(contentUrl)}`;
        return authorizedFetch(url, {
            method: 'GET',
        });
    },

    // Delete uploaded file (V2)
    async deleteFileV2(contentUrl) {
        const url = `${API_BASE_URL}/v2/files/delete?content_url=${encodeURIComponent(contentUrl)}`;
        return authorizedFetch(url, {
            method: 'DELETE',
        });
    },

    // Get current usage status for daily limits
    async getUsageStatus() {
        const url = `${API_BASE_URL}/api/usage/current`;
        return authorizedFetch(url);
    },

    // Get current user information
    async getCurrentUser() {
        const url = `${AUTH_BASE_URL}${USER_CURRENT_PATH}`;
        return authorizedFetch(url);
    },

    // Get membership information
    async getMembershipInfo() {
        const url = `${AUTH_BASE_URL}/api/general/membership/info`;
        return authorizedFetch(url);
    },

    // Promotion APIs (Java backend)
    async getEligiblePromotions() {
        return authorizedFetch(`${API_BASE_URL}/v2/api/promotion/eligible`);
    },

    async redeemPromotion(promotionId) {
        return authorizedFetch(`${API_BASE_URL}/v2/api/promotion/redeem`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ promotion_id: promotionId }),
        });
    },

    // Admin: Tier Window Limits
    async listTierWindowLimits(tierName, isActive) {
        const params = new URLSearchParams();
        if (tierName) params.set('tier_name', tierName);
        if (isActive !== undefined) params.set('is_active', isActive);
        const url = `${API_BASE_URL}/v2/admin/tier-window-limits?${params}`;
        return authorizedFetch(url);
    },

    async createTierWindowLimit(data) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/tier-window-limits`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        });
    },

    async updateTierWindowLimit(id, data) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/tier-window-limits/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        });
    },

    async deleteTierWindowLimit(id) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/tier-window-limits/${id}`, {
            method: 'DELETE',
        });
    },

    // Admin: Budget Adjustments
    async listBudgetAdjustments({ userId, isActive, scope, adjustmentType, page = 0, size = 50 } = {}) {
        const params = new URLSearchParams({ page, size });
        if (userId) params.set('user_id', userId);
        if (isActive !== undefined) params.set('is_active', isActive);
        if (scope) params.set('scope', scope);
        if (adjustmentType) params.set('adjustment_type', adjustmentType);
        return authorizedFetch(`${API_BASE_URL}/v2/admin/budget-adjustments?${params}`);
    },

    async createBudgetAdjustment(data) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/budget-adjustments`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        });
    },

    async updateBudgetAdjustment(id, data) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/budget-adjustments/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        });
    },

    async deleteBudgetAdjustment(id) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/budget-adjustments/${id}`, {
            method: 'DELETE',
        });
    },

    async clearAllBudgetCache() {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/user-tiers/cache/clear-all`, {
            method: 'POST',
        });
    },

    async listTierAssignments(userId) {
        const params = userId ? `?user_id=${userId}` : '';
        return authorizedFetch(`${API_BASE_URL}/v2/admin/tier-assignments${params}`);
    },

    async createTierAssignment(data) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/tier-assignments`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        });
    },

    async deleteTierAssignment(id) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/tier-assignments/${id}`, {
            method: 'DELETE',
        });
    },

    async getUserTierHistory(userId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/user-tiers/${userId}/history`);
    },

    async listBudgetUsage(userId, windowType, activeOnly = true, page = 0, size = 50, usageType) {
        const params = new URLSearchParams({ active_only: activeOnly, page, size });
        if (userId) params.set('user_id', userId);
        if (windowType) params.set('window_type', windowType);
        if (usageType) params.set('usage_type', usageType);
        return authorizedFetch(`${API_BASE_URL}/v2/admin/budget-usage?${params}`);
    },

    async resetUserUsage(userId, windowType) {
        const params = windowType ? `?window_type=${windowType}` : '';
        return authorizedFetch(`${API_BASE_URL}/v2/admin/budget-usage/user/${userId}${params}`, {
            method: 'DELETE',
        });
    },

    async resetAllUsage(windowType) {
        const params = windowType ? `?window_type=${windowType}` : '';
        return authorizedFetch(`${API_BASE_URL}/v2/admin/budget-usage/reset-all${params}`, {
            method: 'DELETE',
        });
    },

    async transcribeAudio(audioBlob, language = 'auto') {
        const formData = new FormData();
        formData.append('file', audioBlob, 'recording.webm');
        formData.append('language', language);

        return authorizedFetch(`${API_BASE_URL}/v2/voice/transcribe`, {
            method: 'POST',
            body: formData,
        });
    }
};

// Export API_BASE_URL for debugging
export { API_BASE_URL, AUTH_BASE_URL, AUTH_LOGIN_PATH, AUTH_SOCIAL_LOGIN_PATH, AUTH_REFRESH_PATH, GOOGLE_CLIENT_ID, FACEBOOK_APP_ID, APPLE_CLIENT_ID, APPLE_REDIRECT_URI, APPLE_CLIENT, AUTH_REGISTER_PATH, AUTH_FORGOT_PASSWORD_PATH };

// Auth API
export const authApi = {
    async login(email, password) {
        const url = `${AUTH_BASE_URL}${AUTH_LOGIN_PATH}`;
        const resp = await fetchWithErrorHandling(url, {
            method: 'POST',
            body: JSON.stringify({ email, password })
        });
        const data = resp?.data || resp;
        if (data?.accessToken) {
            authStorage.set({
                accessToken: data.accessToken,
                refreshToken: data.refreshToken,
                expiredTime: data.expiredTime,
                isExisted: data.isExisted
            });
            try {
                localStorage.setItem(Storage.access_token, data.accessToken);
                localStorage.setItem(Storage.refresh_token, data.refreshToken);
                localStorage.setItem(Storage.expired_time, String(data.expiredTime));
            } catch {}
        }
        return data;
    },
    async register(email, fullName, password, phoneNumber) {
        const url = `${AUTH_BASE_URL}${AUTH_REGISTER_PATH}`;
        return await fetchWithErrorHandling(url, {
            method: 'POST',
            body: JSON.stringify({ email, fullName, password, phoneNumber })
        });
    },
    async forgotPassword(email) {
        const url = `${AUTH_BASE_URL}${AUTH_FORGOT_PASSWORD_PATH}`;
        return await fetchWithErrorHandling(url, {
            method: 'POST',
            body: JSON.stringify({ email })
        });
    },
    async sendEmailOtp(email) {
        const url = `${AUTH_BASE_URL}${AUTH_EMAIL_SEND_OTP_PATH}`;
        return await fetchWithErrorHandling(url, {
            method: 'POST',
            body: JSON.stringify({ email })
        });
    },
    async verifyEmailOtp(email, otp, referralCode = '') {
        const url = `${AUTH_BASE_URL}${AUTH_EMAIL_VERIFY_OTP_PATH}`;
        const resp = await fetchWithErrorHandling(url, {
            method: 'POST',
            body: JSON.stringify({ email, otp, referralCode })
        });
        const data = resp?.data || resp;
        if (data?.accessToken) {
            authStorage.set({
                accessToken: data.accessToken,
                refreshToken: data.refreshToken,
                expiredTime: data.expiredTime,
                isExisted: data.isExisted
            });
            try {
                localStorage.setItem(Storage.access_token, data.accessToken);
                localStorage.setItem(Storage.refresh_token, data.refreshToken);
                localStorage.setItem(Storage.expired_time, String(data.expiredTime));
            } catch { }
        }
        return data;
    },
    async resendEmailOtp(email) {
        const url = `${AUTH_BASE_URL}${AUTH_EMAIL_RESEND_OTP_PATH}`;
        return await fetchWithErrorHandling(url, {
            method: 'POST',
            body: JSON.stringify({ email })
        });
    },
    async verifyOtp(payload) {
        const url = `${AUTH_BASE_URL}${AUTH_VERIFY_OTP_PATH}`;
        const resp = await fetchWithErrorHandling(url, {
            method: 'POST',
            body: JSON.stringify({
                ...payload,
                isToc: true
            })
        });
        const data = resp?.data || resp;
        if (data?.accessToken) {
            authStorage.set({
                accessToken: data.accessToken,
                refreshToken: data.refreshToken,
                expiredTime: data.expiredTime,
                isExisted: data.isExisted
            });
            try {
                localStorage.setItem(Storage.access_token, data.accessToken);
                localStorage.setItem(Storage.refresh_token, data.refreshToken);
                localStorage.setItem(Storage.expired_time, String(data.expiredTime));
            } catch {}
        }
        return data;
    },
    async resendOtp(payload) {
        const url = `${AUTH_BASE_URL}${AUTH_RESEND_OTP_PATH}`;
        return await fetchWithErrorHandling(url, {
            method: 'POST',
            body: JSON.stringify(payload)
        });
    },
    async socialLogin(provider, code, referralCode = '', property = { tickerPdf: '' }) {
        return this.socialLoginRaw({ provider, code, referralCode, property });
    },
    async socialLoginRaw(payload) {
        const url = `${AUTH_BASE_URL}${AUTH_SOCIAL_LOGIN_PATH}`;
        const resp = await fetchWithErrorHandling(url, {
            method: 'POST',
            body: JSON.stringify(payload)
        });
        const data = resp?.data || resp;
        if (data?.accessToken) {
            authStorage.set({
                accessToken: data.accessToken,
                refreshToken: data.refreshToken,
                expiredTime: data.expiredTime,
                isExisted: data.isExisted
            });
            try {
                localStorage.setItem(Storage.access_token, data.accessToken);
                localStorage.setItem(Storage.refresh_token, data.refreshToken);
                localStorage.setItem(Storage.expired_time, String(data.expiredTime));
            } catch {}
        }
        return data;
    },
    async refresh() {
        const token = await refreshAccessToken();
        return token;
    },
    async activeFreeTrial() {
        const url = `${AUTH_BASE_URL}/api/general/membership/active-free-trial`;
        try {
            return await authorizedFetch(url, { method: 'POST' });
        } catch (err) {
            return null;
        }
    },
    logout() {
        authStorage.remove();
    }
};

// Decode JWT payload safely (base64url)
export const decodeJwtPayload = (token) => {
    try {
        const [, payloadPart] = token.split('.');
        if (!payloadPart) return {};
        const normalized = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
        const decoded = atob(normalized.padEnd(normalized.length + (4 - normalized.length % 4) % 4, '='));
        return JSON.parse(decoded);
    } catch {
        return {};
    }
};

// Sharing API (V2)
export const shareApi = {
    // Tạo chia sẻ mới
    async createShare(shareData) {
        const url = `${API_BASE_URL}/v2/shares`;
        return await authorizedFetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(shareData)
        });
    },

    // Lấy shared content (public - không cần auth)
    async getSharedContent(shareId) {
        const url = `${API_BASE_URL}/v2/share/${shareId}`;
        return await fetchWithErrorHandling(url, { method: 'GET' });
    },

    // Lấy danh sách shares của user
    async getUserShares(page = 1, pageSize = 20) {
        const url = `${API_BASE_URL}/v2/users/shares?page=${page}&page_size=${pageSize}`;
        return await authorizedFetch(url, { method: 'GET' });
    },

    // Cập nhật trạng thái share
    async updateShareStatus(shareId, isActive) {
        const url = `${API_BASE_URL}/v2/shares/${shareId}/status`;
        return await authorizedFetch(url, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ is_active: isActive })
        });
    },

    // Xóa share
    async deleteShare(shareId) {
        const url = `${API_BASE_URL}/v2/shares/${shareId}`;
        return await authorizedFetch(url, { method: 'DELETE' });
    },

    // Continue from share - tạo session mới từ shared conversation (V2 NEW)
    async continueFromShare(shareId) {
        const url = `${API_BASE_URL}/v2/share/${shareId}/continue`;
        return await authorizedFetch(url, { method: 'POST' });
    },

    // Quick share session
    async shareSession(sessionId, options = {}) {
        const url = `${API_BASE_URL}/v2/sessions/${sessionId}/share`;
        const params = new URLSearchParams();
        if (options.title) params.append('title', options.title);
        if (options.description) params.append('description', options.description);
        if (options.expires_hours) params.append('expires_hours', options.expires_hours.toString());

        const queryString = params.toString();
        const finalUrl = queryString ? `${url}?${queryString}` : url;

        return await authorizedFetch(finalUrl, { method: 'POST' });
    },

    // Quick share message
    async shareMessage(messageId, options = {}) {
        const url = `${API_BASE_URL}/v2/messages/${messageId}/share`;
        const params = new URLSearchParams();
        if (options.title) params.append('title', options.title);
        if (options.description) params.append('description', options.description);
        if (options.expires_hours) params.append('expires_hours', options.expires_hours.toString());

        const queryString = params.toString();
        const finalUrl = queryString ? `${url}?${queryString}` : url;

        return await authorizedFetch(finalUrl, { method: 'POST' });
    }
};

// Current user API
export const userApi = {
    async getCurrent() {
        const url = `${AUTH_BASE_URL}${USER_CURRENT_PATH}`;
        const resp = await authorizedFetch(url);
        const data = resp?.data || resp;
        try {
            localStorage.setItem(Storage.current_user, JSON.stringify(data));
        } catch {}
        return data;
    },
    getCached() {
        try {
            const raw = localStorage.getItem(Storage.current_user);
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    },
    clearCached() {
        try {
            localStorage.removeItem(Storage.current_user);
        } catch {}
    }
};

// Admin API (requires ROLE_ADMIN)
export const adminApi = {
    // ==================== Agents ====================
    async listAgentOptions() {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/agents/options`);
    },

    async listSkillOptions() {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/skills/options`);
    },

    async listAgents(filters = {}) {
        const params = new URLSearchParams();
        if (filters.agent_type) params.append('agent_type', filters.agent_type);
        if (filters.owner_type) params.append('owner_type', filters.owner_type);
        if (filters.include_inactive) params.append('include_inactive', 'true');

        const query = params.toString();
        const url = `${API_BASE_URL}/v2/admin/agents${query ? `?${query}` : ''}`;
        return authorizedFetch(url);
    },

    async getAgent(agentId) {
        const url = `${API_BASE_URL}/v2/admin/agents/${agentId}`;
        return authorizedFetch(url);
    },

    async getAgentFull(agentId) {
        const url = `${API_BASE_URL}/v2/admin/agents/${agentId}/full`;
        return authorizedFetch(url);
    },

    async createAgent(agentData) {
        const url = `${API_BASE_URL}/v2/admin/agents`;
        return authorizedFetch(url, {
            method: 'POST',
            body: agentData,
        });
    },

    async updateAgent(agentId, agentData) {
        const url = `${API_BASE_URL}/v2/admin/agents/${agentId}`;
        return authorizedFetch(url, {
            method: 'PUT',
            body: agentData,
        });
    },

    async deleteAgent(agentId, force = false) {
        const url = `${API_BASE_URL}/v2/admin/agents/${agentId}${force ? '?force=true' : ''}`;
        return authorizedFetch(url, {
            method: 'DELETE',
        });
    },

    async reloadAgent(agentId) {
        const url = `${API_BASE_URL}/v2/admin/agents/${agentId}/reload`;
        return authorizedFetch(url, {
            method: 'POST',
        });
    },

    async getAgentStats() {
        const url = `${API_BASE_URL}/v2/admin/agents/stats/overview`;
        return authorizedFetch(url);
    },

    async getSystemStats() {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/stats`);
    },

    async getPeriodCounts({ start_date, end_date }) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/stats/period?start_date=${start_date}&end_date=${end_date}`);
    },

    async syncAgentsFromYaml(force = false) {
        const url = `${API_BASE_URL}/v2/admin/agents/sync/yaml${force ? '?force=true' : ''}`;
        return authorizedFetch(url, {
            method: 'POST',
        });
    },

    async clearAgentCache(agentId) {
        const url = `${API_BASE_URL}/v2/admin/agents/${agentId}/cache/clear`;
        return authorizedFetch(url, {
            method: 'POST',
        });
    },

    async seedAgentPrompt(agentId, { useDefault = true, prompt = null, force = false } = {}) {
        const url = `${API_BASE_URL}/v2/admin/agents/${agentId}/seed-prompt`;
        return authorizedFetch(url, {
            method: 'POST',
            body: { use_default: useDefault, prompt, force, confirm: 'confirm' },
        });
    },

    async clearAllAgentCache() {
        const url = `${API_BASE_URL}/v2/admin/agents/cache/clear-all`;
        return authorizedFetch(url, {
            method: 'POST',
        });
    },

    async getAgentCacheStats() {
        const url = `${API_BASE_URL}/v2/admin/agents/cache/stats`;
        return authorizedFetch(url);
    },

    async getInterruptDefaults() {
        const url = `${API_BASE_URL}/v2/admin/agents/interrupt-defaults`;
        return authorizedFetch(url);
    },

    async getAgentDefaults() {
        const url = `${API_BASE_URL}/v2/admin/agents/agent-defaults`;
        return authorizedFetch(url);
    },

    // ==================== Tools ====================
    async listTools(filters = {}) {
        const params = new URLSearchParams();
        if (filters.tag) params.append('tag', filters.tag);
        if (filters.search) params.append('search', filters.search);

        const query = params.toString();
        const url = `${API_BASE_URL}/v2/admin/tools${query ? `?${query}` : ''}`;
        return authorizedFetch(url);
    },

    async getTool(toolName) {
        const url = `${API_BASE_URL}/v2/admin/tools/${encodeURIComponent(toolName)}`;
        return authorizedFetch(url);
    },

    async listToolkits() {
        const url = `${API_BASE_URL}/v2/admin/tools/toolkits`;
        return authorizedFetch(url);
    },

    async getToolkitTools(toolkitName) {
        const url = `${API_BASE_URL}/v2/admin/tools/toolkit/${encodeURIComponent(toolkitName)}`;
        return authorizedFetch(url);
    },

    async invokeTool(toolName, params = {}) {
        const url = `${API_BASE_URL}/v2/admin/tools/${encodeURIComponent(toolName)}/invoke`;
        return authorizedFetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ params }),
        });
    },

    // ==================== Skills ====================
    async listSkills(filters = {}) {
        const params = new URLSearchParams();
        if (filters.search) params.append('search', filters.search);

        const query = params.toString();
        const url = `${API_BASE_URL}/v2/admin/skills${query ? `?${query}` : ''}`;
        return authorizedFetch(url);
    },

    async getSkill(skillId) {
        const url = `${API_BASE_URL}/v2/admin/skills/${encodeURIComponent(skillId)}`;
        return authorizedFetch(url);
    },

    async createSkill(skillData) {
        const url = `${API_BASE_URL}/v2/admin/skills`;
        return authorizedFetch(url, {
            method: 'POST',
            body: skillData,
        });
    },

    async updateSkill(skillId, skillData) {
        const url = `${API_BASE_URL}/v2/admin/skills/${encodeURIComponent(skillId)}`;
        return authorizedFetch(url, {
            method: 'PUT',
            body: skillData,
        });
    },

    async deleteSkill(skillId, force = false) {
        const url = `${API_BASE_URL}/v2/admin/skills/${encodeURIComponent(skillId)}${force ? '?force=true' : ''}`;
        return authorizedFetch(url, {
            method: 'DELETE',
        });
    },

    async previewUploadSkill({ zipFile = null, folderFiles = [], paths = null } = {}) {
        const formData = new FormData();
        if (zipFile) {
            formData.append('files', zipFile);
        } else {
            folderFiles.forEach(f => formData.append('files', f));
        }
        if (paths) {
            formData.append('paths', JSON.stringify(paths));
        }
        const url = `${API_BASE_URL}/v2/admin/skills/upload/preview`;
        return authorizedFetch(url, { method: 'POST', body: formData });
    },

    async uploadSkill({ zipFile = null, folderFiles = [], paths = null, overwrite = false } = {}) {
        const formData = new FormData();
        if (zipFile) {
            formData.append('files', zipFile);
        } else {
            folderFiles.forEach(f => formData.append('files', f));
        }
        if (paths) {
            formData.append('paths', JSON.stringify(paths));
        }
        formData.append('overwrite', overwrite.toString());
        const url = `${API_BASE_URL}/v2/admin/skills/upload`;
        return authorizedFetch(url, { method: 'POST', body: formData });
    },

    async getGithubConfig() {
        const url = `${API_BASE_URL}/v2/admin/skills/github/config`;
        return authorizedFetch(url);
    },

    async saveGithubConfig({ url, paths = [], branch = '', token = null } = {}) {
        const apiUrl = `${API_BASE_URL}/v2/admin/skills/github/config`;
        return authorizedFetch(apiUrl, {
            method: 'PUT',
            body: { url, paths, branch, token },
        });
    },

    async testGithubConnection({ url, paths = [], branch = '', token = null } = {}) {
        const apiUrl = `${API_BASE_URL}/v2/admin/skills/github/test-connection`;
        return authorizedFetch(apiUrl, {
            method: 'POST',
            body: { url, paths, branch, token },
        });
    },

    async previewGithubSkills({ url = null, token = null, paths = null, branch = null } = {}) {
        const apiUrl = `${API_BASE_URL}/v2/admin/skills/github/preview`;
        return authorizedFetch(apiUrl, {
            method: 'POST',
            body: { url, token, paths, branch },
        });
    },

    async syncGithubSkills({ url = null, token = null, paths = null, branch = null, skillIds = null, overwrite = false } = {}) {
        const apiUrl = `${API_BASE_URL}/v2/admin/skills/github/sync`;
        return authorizedFetch(apiUrl, {
            method: 'POST',
            body: { url, token, paths, branch, skill_ids: skillIds, overwrite },
        });
    },

    // ==================== Skill Resources ====================
    async listSkillResources(skillId, folder = null) {
        const params = folder ? `?folder=${encodeURIComponent(folder)}` : '';
        const url = `${API_BASE_URL}/v2/admin/skills/${encodeURIComponent(skillId)}/resources${params}`;
        return authorizedFetch(url);
    },

    async getSkillResource(skillId, resourcePath) {
        const url = `${API_BASE_URL}/v2/admin/skills/${encodeURIComponent(skillId)}/resources/${resourcePath}`;
        const response = await authorizedFetch(url, { raw: true });
        return response;
    },

    async uploadSkillResources(skillId, files, folder, paths = null) {
        const formData = new FormData();
        for (const file of files) {
            formData.append('files', file);
        }
        if (paths) {
            formData.append('paths', JSON.stringify(paths));
        } else if (folder) {
            formData.append('folder', folder);
        }
        const url = `${API_BASE_URL}/v2/admin/skills/${encodeURIComponent(skillId)}/resources`;
        return authorizedFetch(url, { method: 'POST', body: formData });
    },

    async updateSkillResource(skillId, resourcePath, file) {
        const formData = new FormData();
        formData.append('file', file);
        const url = `${API_BASE_URL}/v2/admin/skills/${encodeURIComponent(skillId)}/resources/${resourcePath}`;
        return authorizedFetch(url, { method: 'PUT', body: formData });
    },

    async createSkillFolder(skillId, folderPath) {
        const formData = new FormData();
        formData.append('folder_path', folderPath);
        const url = `${API_BASE_URL}/v2/admin/skills/${encodeURIComponent(skillId)}/folders`;
        return authorizedFetch(url, { method: 'POST', body: formData });
    },

    async renameSkillResource(skillId, resourcePath, newPath) {
        const formData = new FormData();
        formData.append('new_path', newPath);
        const url = `${API_BASE_URL}/v2/admin/skills/${encodeURIComponent(skillId)}/resources/${resourcePath}/rename`;
        return authorizedFetch(url, { method: 'POST', body: formData });
    },

    async renameSkillFolder(skillId, folderPath, newName) {
        const formData = new FormData();
        formData.append('new_name', newName);
        const url = `${API_BASE_URL}/v2/admin/skills/${encodeURIComponent(skillId)}/folders/${folderPath}/rename`;
        return authorizedFetch(url, { method: 'POST', body: formData });
    },

    async deleteSkillFolder(skillId, folderPath) {
        const url = `${API_BASE_URL}/v2/admin/skills/${encodeURIComponent(skillId)}/folders/${folderPath}`;
        return authorizedFetch(url, { method: 'DELETE' });
    },

    async deleteSkillResource(skillId, resourcePath) {
        const url = `${API_BASE_URL}/v2/admin/skills/${encodeURIComponent(skillId)}/resources/${resourcePath}`;
        return authorizedFetch(url, { method: 'DELETE' });
    },

    // ==================== Subagents ====================
    async listSubagents(filters = {}) {
        const params = new URLSearchParams();
        if (filters.search) params.append('search', filters.search);

        const query = params.toString();
        const url = `${API_BASE_URL}/v2/admin/subagents${query ? `?${query}` : ''}`;
        return authorizedFetch(url);
    },

    async getSubagent(subagentId) {
        const url = `${API_BASE_URL}/v2/admin/subagents/${encodeURIComponent(subagentId)}`;
        return authorizedFetch(url);
    },

    async createSubagent(subagentData) {
        const url = `${API_BASE_URL}/v2/admin/subagents`;
        return authorizedFetch(url, {
            method: 'POST',
            body: subagentData,
        });
    },

    async updateSubagent(subagentId, subagentData) {
        const url = `${API_BASE_URL}/v2/admin/subagents/${encodeURIComponent(subagentId)}`;
        return authorizedFetch(url, {
            method: 'PUT',
            body: subagentData,
        });
    },

    async deleteSubagent(subagentId, force = false) {
        const url = `${API_BASE_URL}/v2/admin/subagents/${encodeURIComponent(subagentId)}${force ? '?force=true' : ''}`;
        return authorizedFetch(url, {
            method: 'DELETE',
        });
    },

    // ==================== Knowledge Files ====================
    async listKnowledge(filters = {}) {
        const params = new URLSearchParams();
        if (filters.search) params.append('search', filters.search);
        if (filters.agent_id) params.append('agent_id', filters.agent_id);
        if (filters.include_inactive) params.append('include_inactive', 'true');
        const query = params.toString();
        return authorizedFetch(`${API_BASE_URL}/v2/admin/knowledge${query ? `?${query}` : ''}`);
    },

    async getKnowledge(knowledgeId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/knowledge/${encodeURIComponent(knowledgeId)}`);
    },

    async createKnowledge(data) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/knowledge`, {
            method: 'POST',
            body: data,
        });
    },

    async updateKnowledge(knowledgeId, data) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/knowledge/${encodeURIComponent(knowledgeId)}`, {
            method: 'PUT',
            body: data,
        });
    },

    async deleteKnowledge(knowledgeId, force = false) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/knowledge/${encodeURIComponent(knowledgeId)}${force ? '?force=true' : ''}`, {
            method: 'DELETE',
        });
    },

    // ==================== MCP Servers ====================
    async listMCPServers(filters = {}) {
        const params = new URLSearchParams();
        if (filters.search) params.append('search', filters.search);
        if (filters.transport_type) params.append('transport_type', filters.transport_type);
        if (filters.owner_type) params.append('owner_type', filters.owner_type);
        if (filters.include_inactive) params.append('include_inactive', 'true');

        const query = params.toString();
        const url = `${API_BASE_URL}/v2/admin/mcp${query ? `?${query}` : ''}`;
        return authorizedFetch(url);
    },

    async getMCPServer(mcpId) {
        const url = `${API_BASE_URL}/v2/admin/mcp/${encodeURIComponent(mcpId)}`;
        return authorizedFetch(url);
    },

    async createMCPServer(serverData) {
        const url = `${API_BASE_URL}/v2/admin/mcp`;
        return authorizedFetch(url, {
            method: 'POST',
            body: serverData,
        });
    },

    async updateMCPServer(mcpId, serverData) {
        const url = `${API_BASE_URL}/v2/admin/mcp/${encodeURIComponent(mcpId)}`;
        return authorizedFetch(url, {
            method: 'PUT',
            body: serverData,
        });
    },

    async deleteMCPServer(mcpId, force = false) {
        const url = `${API_BASE_URL}/v2/admin/mcp/${encodeURIComponent(mcpId)}${force ? '?force=true' : ''}`;
        return authorizedFetch(url, {
            method: 'DELETE',
        });
    },

    async reloadMCPServers() {
        const url = `${API_BASE_URL}/v2/admin/mcp/reload`;
        return authorizedFetch(url, { method: 'POST' });
    },

    async seedMCPServers() {
        const url = `${API_BASE_URL}/v2/admin/mcp/seed`;
        return authorizedFetch(url, { method: 'POST' });
    },

    async testMCPServer(mcpId) {
        const url = `${API_BASE_URL}/v2/admin/mcp/${encodeURIComponent(mcpId)}/test`;
        return authorizedFetch(url, { method: 'POST' });
    },

    // ==================== Sync (2-Phase) ====================
    async getSyncStatus() {
        const url = `${API_BASE_URL}/v2/admin/sync/status`;
        return authorizedFetch(url);
    },

    async syncFilesSkills(force = false) {
        const url = `${API_BASE_URL}/v2/admin/sync/files/skills${force ? '?force=true' : ''}`;
        return authorizedFetch(url, { method: 'POST' });
    },

    async syncFilesSubagents(force = false) {
        const url = `${API_BASE_URL}/v2/admin/sync/files/subagents${force ? '?force=true' : ''}`;
        return authorizedFetch(url, { method: 'POST' });
    },

    async syncFilesAll(force = false) {
        const url = `${API_BASE_URL}/v2/admin/sync/files/all${force ? '?force=true' : ''}`;
        return authorizedFetch(url, { method: 'POST' });
    },

    async syncS3Skills() {
        const url = `${API_BASE_URL}/v2/admin/sync/s3/skills`;
        return authorizedFetch(url, { method: 'POST' });
    },

    async syncS3Subagents() {
        const url = `${API_BASE_URL}/v2/admin/sync/s3/subagents`;
        return authorizedFetch(url, { method: 'POST' });
    },


    async syncS3All() {
        const url = `${API_BASE_URL}/v2/admin/sync/s3/all`;
        return authorizedFetch(url, { method: 'POST' });
    },

    async runMigrations() {
        const url = `${API_BASE_URL}/v2/admin/sync/migrate`;
        return authorizedFetch(url, { method: 'POST' });
    },

    async runSeeds() {
        const url = `${API_BASE_URL}/v2/admin/sync/seed`;
        return authorizedFetch(url, { method: 'POST' });
    },

    // ==================== Model Costs ====================
    async listModelCosts(filters = {}) {
        const params = new URLSearchParams();
        if (filters.provider) params.append('provider', filters.provider);
        if (filters.model_name) params.append('model_name', filters.model_name);
        const query = params.toString();
        return authorizedFetch(`${API_BASE_URL}/v2/admin/model-costs${query ? `?${query}` : ''}`);
    },

    async getModelCost(costId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/model-costs/${costId}`);
    },

    async createModelCost(data) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/model-costs`, { method: 'POST', body: data });
    },

    async updateModelCost(costId, data) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/model-costs/${costId}`, { method: 'PUT', body: data });
    },

    async deleteModelCost(costId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/model-costs/${costId}`, { method: 'DELETE' });
    },

    // ==================== User Tiers ====================
    async listUserTiers(filters = {}) {
        const params = new URLSearchParams();
        if (filters.tier_name) params.append('tier_name', filters.tier_name);
        if (filters.search) params.append('search', filters.search);
        if (filters.limit) params.append('limit', filters.limit);
        if (filters.offset) params.append('offset', filters.offset);
        const query = params.toString();
        return authorizedFetch(`${API_BASE_URL}/v2/admin/user-tiers${query ? `?${query}` : ''}`);
    },

    async getUserTier(userId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/user-tiers/${encodeURIComponent(userId)}`);
    },

    async setUserTier(userId, data) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/user-tiers/${encodeURIComponent(userId)}`, { method: 'PUT', body: data });
    },

    async bulkSetTier(data) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/user-tiers/bulk`, { method: 'POST', body: data });
    },

    async deleteUserTier(userId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/user-tiers/${encodeURIComponent(userId)}`, { method: 'DELETE' });
    },

    async getTierDistribution() {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/user-tiers/stats/summary`);
    },

    // ==================== System Configs ====================
    async listSystemConfigs(category) {
        const params = category ? `?category=${encodeURIComponent(category)}` : '';
        return authorizedFetch(`${API_BASE_URL}/v2/admin/system-configs${params}`);
    },

    async getSystemConfig(configKey) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/system-configs/${encodeURIComponent(configKey)}`);
    },

    async createSystemConfig(data) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/system-configs`, { method: 'POST', body: data });
    },

    async updateSystemConfig(configKey, data) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/system-configs/${encodeURIComponent(configKey)}`, { method: 'PUT', body: data });
    },

    async deleteSystemConfig(configKey) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/system-configs/${encodeURIComponent(configKey)}`, { method: 'DELETE' });
    },

    // ==================== Token Usage ====================
    async getTokenUsageOverview(params = {}) {
        const p = new URLSearchParams();
        if (params.start_date) p.append('start_date', params.start_date);
        if (params.end_date) p.append('end_date', params.end_date);
        if (!params.start_date) p.append('days', params.days || 7);
        return authorizedFetch(`${API_BASE_URL}/v2/admin/token-usage/overview?${p.toString()}`);
    },

    async getTokenUsageByDay(params = {}) {
        const p = new URLSearchParams();
        if (params.start_date) p.append('start_date', params.start_date);
        if (params.end_date) p.append('end_date', params.end_date);
        if (!params.start_date) p.append('days', params.days || 30);
        if (params.user_id) p.append('user_id', params.user_id);
        return authorizedFetch(`${API_BASE_URL}/v2/admin/token-usage/by-day?${p.toString()}`);
    },

    async getTokenUsageByUser(params = {}) {
        const p = new URLSearchParams();
        if (params.start_date) p.append('start_date', params.start_date);
        if (params.end_date) p.append('end_date', params.end_date);
        if (!params.start_date) p.append('days', params.days || 7);
        if (params.limit) p.append('limit', params.limit);
        if (params.offset) p.append('offset', params.offset);
        return authorizedFetch(`${API_BASE_URL}/v2/admin/token-usage/by-user?${p.toString()}`);
    },

    async getTokenUsageByModel(params = {}) {
        const p = new URLSearchParams();
        if (params.start_date) p.append('start_date', params.start_date);
        if (params.end_date) p.append('end_date', params.end_date);
        if (!params.start_date) p.append('days', params.days || 7);
        if (params.user_id) p.append('user_id', params.user_id);
        return authorizedFetch(`${API_BASE_URL}/v2/admin/token-usage/by-model?${p.toString()}`);
    },

    async getTokenUsageLogs(filters = {}) {
        const params = new URLSearchParams();
        if (filters.start_date) params.append('start_date', filters.start_date);
        if (filters.end_date) params.append('end_date', filters.end_date);
        if (!filters.start_date) params.append('days', filters.days || 7);
        if (filters.limit) params.append('limit', filters.limit);
        if (filters.offset) params.append('offset', filters.offset);
        if (filters.user_id) params.append('user_id', filters.user_id);
        if (filters.session_id) params.append('session_id', filters.session_id);
        if (filters.model_name) params.append('model_name', filters.model_name);
        if (filters.agent_name) params.append('agent_name', filters.agent_name);
        return authorizedFetch(`${API_BASE_URL}/v2/admin/token-usage/logs?${params.toString()}`);
    },

    async getTokenUsageLog(logId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/token-usage/logs/${logId}`);
    },

    async getTrackingHealth() {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/token-usage/health`);
    },

    async getTokenUsageByAgent(params = {}) {
        const p = new URLSearchParams();
        if (params.start_date) p.append('start_date', params.start_date);
        if (params.end_date) p.append('end_date', params.end_date);
        if (!params.start_date) p.append('days', params.days || 7);
        return authorizedFetch(`${API_BASE_URL}/v2/admin/token-usage/by-agent?${p.toString()}`);
    },

    async getTokenUsageCacheStats(params = {}) {
        const p = new URLSearchParams();
        if (params.start_date) p.append('start_date', params.start_date);
        if (params.end_date) p.append('end_date', params.end_date);
        if (!params.start_date) p.append('days', params.days || 7);
        return authorizedFetch(`${API_BASE_URL}/v2/admin/token-usage/cache-stats?${p.toString()}`);
    },

    async getTokenUsageSessionStats(params = {}) {
        const p = new URLSearchParams();
        if (params.start_date) p.append('start_date', params.start_date);
        if (params.end_date) p.append('end_date', params.end_date);
        if (!params.start_date) p.append('days', params.days || 7);
        return authorizedFetch(`${API_BASE_URL}/v2/admin/token-usage/session-stats?${p.toString()}`);
    },

    async getTokenUsageResponseTime(params = {}) {
        const p = new URLSearchParams();
        if (params.start_date) p.append('start_date', params.start_date);
        if (params.end_date) p.append('end_date', params.end_date);
        if (!params.start_date) p.append('days', params.days || 7);
        return authorizedFetch(`${API_BASE_URL}/v2/admin/token-usage/response-time-stats?${p.toString()}`);
    },

    async getTokenUsageHourlyHeatmap(params = {}) {
        const p = new URLSearchParams();
        if (params.start_date) p.append('start_date', params.start_date);
        if (params.end_date) p.append('end_date', params.end_date);
        if (!params.start_date) p.append('days', params.days || 7);
        return authorizedFetch(`${API_BASE_URL}/v2/admin/token-usage/hourly-heatmap?${p.toString()}`);
    },

    async getTokenUsageDailyBreakdown(params = {}) {
        const p = new URLSearchParams();
        if (params.group_by) p.append('group_by', params.group_by);
        if (params.start_date) p.append('start_date', params.start_date);
        if (params.end_date) p.append('end_date', params.end_date);
        if (!params.start_date) p.append('days', params.days || 7);
        return authorizedFetch(`${API_BASE_URL}/v2/admin/token-usage/daily-breakdown?${p.toString()}`);
    },

    async getSessionUsers(params = {}) {
        const p = new URLSearchParams();
        if (params.search) p.append('search', params.search);
        if (params.sort_by) p.append('sort_by', params.sort_by);
        if (params.limit) p.append('limit', params.limit);
        if (params.offset != null) p.append('offset', params.offset);
        return authorizedFetch(`${API_BASE_URL}/v2/admin/sessions/users?${p.toString()}`);
    },

    async getSessionList(params = {}) {
        const p = new URLSearchParams();
        if (params.session_id) p.append('session_id', params.session_id);
        if (params.user_id) p.append('user_id', params.user_id);
        if (params.search) p.append('search', params.search);
        if (params.start_date) p.append('start_date', params.start_date);
        if (params.end_date) p.append('end_date', params.end_date);
        if (params.limit) p.append('limit', params.limit);
        if (params.offset != null) p.append('offset', params.offset);
        return authorizedFetch(`${API_BASE_URL}/v2/admin/sessions?${p.toString()}`);
    },

    async getSessionMessages(sessionId, params = {}) {
        const p = new URLSearchParams();
        if (params.type) p.append('type', params.type);
        if (params.status) p.append('status', params.status);
        if (params.limit) p.append('limit', params.limit);
        if (params.offset != null) p.append('offset', params.offset);
        return authorizedFetch(`${API_BASE_URL}/v2/admin/sessions/${encodeURIComponent(sessionId)}/messages?${p.toString()}`);
    },

    async getSessionTokenUsage(sessionId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/sessions/${encodeURIComponent(sessionId)}/token-usage`);
    },

    async getSessionView(sessionId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/sessions/${encodeURIComponent(sessionId)}/view`);
    },

    // ==================== Long-term Memory (Store) ====================
    async listLongMemoryItems(params = {}) {
        const p = new URLSearchParams();
        if (params.user_id) p.append('user_id', params.user_id);
        if (params.search) p.append('search', params.search);
        if (params.limit) p.append('limit', params.limit);
        if (params.offset != null) p.append('offset', params.offset);
        const q = p.toString();
        return authorizedFetch(`${API_BASE_URL}/v2/admin/long-memory/items${q ? `?${q}` : ''}`);
    },

    async getLongMemoryItem(prefix, key) {
        const p = new URLSearchParams({ prefix, key });
        return authorizedFetch(`${API_BASE_URL}/v2/admin/long-memory/items/detail?${p.toString()}`);
    },

    async deleteLongMemoryItem(prefix, key) {
        const p = new URLSearchParams({ prefix, key });
        return authorizedFetch(`${API_BASE_URL}/v2/admin/long-memory/items?${p.toString()}`, { method: 'DELETE' });
    },

    // ==================== Content URLs ====================
    async listContentUrls(params = {}) {
        const p = new URLSearchParams();
        if (params.user_id) p.append('user_id', params.user_id);
        if (params.status) p.append('status', params.status);
        if (params.source_type) p.append('source_type', params.source_type);
        if (params.search) p.append('search', params.search);
        if (params.limit) p.append('limit', params.limit);
        if (params.offset != null) p.append('offset', params.offset);
        const q = p.toString();
        return authorizedFetch(`${API_BASE_URL}/v2/admin/content-urls${q ? `?${q}` : ''}`);
    },

    async getContentUrl(id) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/content-urls/${id}`);
    },

    async getContentUrlDownload(id, expiry = 3600) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/content-urls/${id}/download-url?expiry=${expiry}`);
    },

    async deleteContentUrl(id) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/content-urls/${id}`, { method: 'DELETE' });
    },

    // ==================== Artifacts ====================
    async listArtifacts(params = {}) {
        const p = new URLSearchParams();
        if (params.user_id) p.append('user_id', params.user_id);
        if (params.session_id) p.append('session_id', params.session_id);
        if (params.artifact_type) p.append('artifact_type', params.artifact_type);
        if (params.include_deleted) p.append('include_deleted', 'true');
        if (params.limit) p.append('limit', params.limit);
        if (params.offset != null) p.append('offset', params.offset);
        const q = p.toString();
        return authorizedFetch(`${API_BASE_URL}/v2/admin/artifacts${q ? `?${q}` : ''}`);
    },

    async getArtifact(id) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/artifacts/${encodeURIComponent(id)}`);
    },

    async deleteArtifact(id) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/artifacts/${encodeURIComponent(id)}`, { method: 'DELETE' });
    },

    // ==================== Shares ====================
    async listShares(params = {}) {
        const p = new URLSearchParams();
        if (params.user_id) p.append('user_id', params.user_id);
        if (params.is_active != null) p.append('is_active', params.is_active);
        if (params.search) p.append('search', params.search);
        if (params.limit) p.append('limit', params.limit);
        if (params.offset != null) p.append('offset', params.offset);
        const q = p.toString();
        return authorizedFetch(`${API_BASE_URL}/v2/admin/shares${q ? `?${q}` : ''}`);
    },

    async getShare(shareId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/shares/${encodeURIComponent(shareId)}`);
    },

    async updateShareStatus(shareId, isActive) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/shares/${encodeURIComponent(shareId)}/status?is_active=${isActive}`, { method: 'PATCH' });
    },

    async deleteShare(shareId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/shares/${encodeURIComponent(shareId)}`, { method: 'DELETE' });
    },

    // ==================== Checkpoints ====================
    async getCheckpointStats() {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/checkpoints/stats`);
    },

    async getCheckpointThreads(params = {}) {
        const p = new URLSearchParams();
        if (params.search) p.append('search', params.search);
        if (params.limit) p.append('limit', params.limit);
        if (params.offset != null) p.append('offset', params.offset);
        const q = p.toString();
        return authorizedFetch(`${API_BASE_URL}/v2/admin/checkpoints/threads${q ? `?${q}` : ''}`);
    },

    async getCheckpointTimeline(threadId, params = {}) {
        const p = new URLSearchParams();
        if (params.checkpoint_ns != null) p.append('checkpoint_ns', params.checkpoint_ns);
        if (params.limit) p.append('limit', params.limit);
        if (params.offset != null) p.append('offset', params.offset);
        const q = p.toString();
        return authorizedFetch(`${API_BASE_URL}/v2/admin/checkpoints/threads/${encodeURIComponent(threadId)}/checkpoints${q ? `?${q}` : ''}`);
    },

    async getCheckpointDetail(threadId, checkpointId, params = {}) {
        const p = new URLSearchParams();
        if (params.checkpoint_ns != null) p.append('checkpoint_ns', params.checkpoint_ns);
        const q = p.toString();
        return authorizedFetch(`${API_BASE_URL}/v2/admin/checkpoints/threads/${encodeURIComponent(threadId)}/checkpoints/${encodeURIComponent(checkpointId)}${q ? `?${q}` : ''}`);
    },

    async getCheckpointWrites(threadId, params = {}) {
        const p = new URLSearchParams();
        if (params.checkpoint_ns != null) p.append('checkpoint_ns', params.checkpoint_ns);
        if (params.checkpoint_id) p.append('checkpoint_id', params.checkpoint_id);
        if (params.channel) p.append('channel', params.channel);
        if (params.decode) p.append('decode', 'true');
        if (params.limit) p.append('limit', params.limit);
        if (params.offset != null) p.append('offset', params.offset);
        const q = p.toString();
        return authorizedFetch(`${API_BASE_URL}/v2/admin/checkpoints/threads/${encodeURIComponent(threadId)}/writes${q ? `?${q}` : ''}`);
    },

    async getCheckpointBlobs(threadId, params = {}) {
        const p = new URLSearchParams();
        if (params.decode) p.append('decode', 'true');
        if (params.limit) p.append('limit', params.limit);
        if (params.offset != null) p.append('offset', params.offset);
        const q = p.toString();
        return authorizedFetch(`${API_BASE_URL}/v2/admin/checkpoints/threads/${encodeURIComponent(threadId)}/blobs${q ? `?${q}` : ''}`);
    },

    // ==================== User Management ====================
    async getUserSummary(userId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/users/${encodeURIComponent(userId)}/summary`);
    },

    async getUserOverview(userId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/users/${encodeURIComponent(userId)}/overview`);
    },

    async getUserSessions(userId, params = {}) {
        const p = new URLSearchParams();
        if (params.limit) p.append('limit', params.limit);
        if (params.offset != null) p.append('offset', params.offset);
        if (params.search) p.append('search', params.search);
        const q = p.toString();
        return authorizedFetch(`${API_BASE_URL}/v2/admin/users/${encodeURIComponent(userId)}/sessions${q ? `?${q}` : ''}`);
    },

    async getUserCostUsage(userId, params = {}) {
        const p = new URLSearchParams();
        if (params.period) p.append('period', params.period);
        const q = p.toString();
        return authorizedFetch(`${API_BASE_URL}/v2/admin/users/${encodeURIComponent(userId)}/cost-usage${q ? `?${q}` : ''}`);
    },

    async getUserContent(userId, params = {}) {
        const p = new URLSearchParams();
        if (params.limit) p.append('limit', params.limit);
        if (params.offset != null) p.append('offset', params.offset);
        if (params.content_type) p.append('content_type', params.content_type);
        const q = p.toString();
        return authorizedFetch(`${API_BASE_URL}/v2/admin/users/${encodeURIComponent(userId)}/content${q ? `?${q}` : ''}`);
    },

    async getUserActivity(userId, params = {}) {
        const p = new URLSearchParams();
        if (params.limit) p.append('limit', params.limit);
        if (params.offset != null) p.append('offset', params.offset);
        if (params.type) p.append('type', params.type);
        const q = p.toString();
        return authorizedFetch(`${API_BASE_URL}/v2/admin/users/${encodeURIComponent(userId)}/activity${q ? `?${q}` : ''}`);
    },

    async getUserInsights(userId, params = {}) {
        const p = new URLSearchParams();
        if (params.days) p.append('days', params.days);
        const q = p.toString();
        return authorizedFetch(`${API_BASE_URL}/v2/admin/users/${encodeURIComponent(userId)}/insights${q ? `?${q}` : ''}`);
    },

    async getUserUsageStatus(userId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/users/${encodeURIComponent(userId)}/usage-status`);
    },

    async getUserUsageHistory(userId, limit = 30) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/users/${encodeURIComponent(userId)}/usage-history?limit=${limit}`);
    },

    async getRAGOverview() {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/rag/overview`);
    },

    async getRAGMetrics() {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/rag/metrics`);
    },

    async listRAGCollections(filters = {}) {
        const params = new URLSearchParams();
        if (filters.search) params.append('search', filters.search);
        const query = params.toString();
        return authorizedFetch(`${API_BASE_URL}/v2/admin/rag/collections${query ? `?${query}` : ''}`);
    },

    async getRAGCollection(collectionId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/rag/collections/${encodeURIComponent(collectionId)}`);
    },

    async createRAGCollection(data) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/rag/collections`, {
            method: 'POST',
            body: data,
        });
    },

    async updateRAGCollection(collectionId, data) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/rag/collections/${encodeURIComponent(collectionId)}`, {
            method: 'PUT',
            body: data,
        });
    },

    async deleteRAGCollection(collectionId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/rag/collections/${encodeURIComponent(collectionId)}`, {
            method: 'DELETE',
        });
    },

    async listRAGDocuments(collectionId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/rag/collections/${encodeURIComponent(collectionId)}/documents`);
    },

    async getRAGDocument(documentId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/rag/documents/${encodeURIComponent(documentId)}`);
    },

    async createRAGDocument(collectionId, data) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/rag/collections/${encodeURIComponent(collectionId)}/documents`, {
            method: 'POST',
            body: data,
        });
    },

    async updateRAGDocument(documentId, data) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/rag/documents/${encodeURIComponent(documentId)}`, {
            method: 'PUT',
            body: data,
        });
    },

    async deleteRAGDocument(documentId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/rag/documents/${encodeURIComponent(documentId)}`, {
            method: 'DELETE',
        });
    },

    async indexRAGDocument(documentId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/rag/documents/${encodeURIComponent(documentId)}/index`, {
            method: 'POST',
        });
    },

    async reindexRAGCollection(collectionId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/rag/collections/${encodeURIComponent(collectionId)}/reindex`, {
            method: 'POST',
        });
    },

    async searchRAG(data) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/rag/search`, {
            method: 'POST',
            body: data,
        });
    },

    async getRAGSettings() {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/rag/settings`);
    },

    async updateRAGSettings(data) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/rag/settings`, {
            method: 'PUT',
            body: data,
        });
    },

    async listVectorStores() {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/rag/vector-stores`);
    },

    async listVectorStoreIndexes(provider) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/rag/vector-stores/${encodeURIComponent(provider)}/indexes`);
    },

    async browseVectors(provider, indexName, { limit = 20, nextToken } = {}) {
        const params = new URLSearchParams({ limit });
        if (nextToken) params.append('next_token', nextToken);
        return authorizedFetch(`${API_BASE_URL}/v2/admin/rag/vector-stores/${encodeURIComponent(provider)}/indexes/${encodeURIComponent(indexName)}/vectors?${params}`);
    },

    async queryVectorStore(provider, indexName, data) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/rag/vector-stores/${encodeURIComponent(provider)}/indexes/${encodeURIComponent(indexName)}/query`, {
            method: 'POST',
            body: data,
        });
    },

    async getMonitorNodes() {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/monitor/nodes`);
    },

    async getNodeCache(pid) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/monitor/nodes/${pid}/cache`);
    },

    async getResolvedCache(agentId) {
        const params = agentId ? `?agent_id=${encodeURIComponent(agentId)}` : '';
        return authorizedFetch(`${API_BASE_URL}/v2/admin/monitor/resolved-cache${params}`);
    },

    async reloadAllCaches() {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/monitor/reload`, { method: 'POST' });
    },

    async listRedisKeys({ pattern = '*', cursor = 0, limit = 50 } = {}) {
        const p = new URLSearchParams({ pattern, cursor, limit });
        return authorizedFetch(`${API_BASE_URL}/v2/admin/monitor/redis/keys?${p.toString()}`);
    },

    async getRedisKeyDetail(key) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/monitor/redis/keys/${encodeURIComponent(key)}`);
    },

    async listLocalCache({ pid, cache_type, search } = {}) {
        const p = new URLSearchParams();
        if (pid) p.append('pid', pid);
        if (cache_type) p.append('cache_type', cache_type);
        if (search) p.append('search', search);
        return authorizedFetch(`${API_BASE_URL}/v2/admin/monitor/local-cache?${p.toString()}`);
    },

    // ==================== WS Monitor ====================
    async getWSOverview() {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/ws-monitor/overview`);
    },

    async getWSConnections() {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/ws-monitor/connections`);
    },

    async getWSTasks() {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/ws-monitor/tasks`);
    },

    async killWSTask(sessionId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/ws-monitor/tasks/${sessionId}/kill`, { method: 'POST' });
    },

    async disconnectWSConnection(connectionId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/ws-monitor/connections/${encodeURIComponent(connectionId)}/disconnect`, { method: 'POST' });
    },

    async getWSTaskEvents(sessionId, limit = 100) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/ws-monitor/tasks/${sessionId}/events?limit=${limit}`);
    },

    async broadcastWSMessage(message) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/ws-monitor/broadcast`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ message }),
        });
    },

    async getWSStreams() {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/ws-monitor/streams`);
    },

    async cleanupWSStream(sessionId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/ws-monitor/streams/${sessionId}`, { method: 'DELETE' });
    },

    // ==================== Snapshots ====================
    async listSnapshots(params = {}) {
        const p = new URLSearchParams();
        if (params.page) p.append('page', params.page);
        if (params.limit) p.append('limit', params.limit);
        const q = p.toString();
        return authorizedFetch(`${API_BASE_URL}/v2/admin/snapshots${q ? `?${q}` : ''}`);
    },

    async getSnapshot(name) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/snapshots/${encodeURIComponent(name)}`);
    },

    async getDefaultPackages() {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/snapshots/packages`);
    },

    async createSnapshot(data) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/snapshots`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        });
    },

    async deleteSnapshot(name) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/snapshots/${encodeURIComponent(name)}`, { method: 'DELETE' });
    },

    // ==================== Gemini Cache ====================
    async listGeminiCaches(params = {}) {
        const p = new URLSearchParams();
        if (params.ctx_type) p.append('ctx_type', params.ctx_type);
        if (params.status) p.append('status', params.status);
        if (params.user_id) p.append('user_id', params.user_id);
        if (params.q) p.append('q', params.q);
        if (params.limit) p.append('limit', params.limit);
        if (params.offset) p.append('offset', params.offset);
        const q = p.toString();
        return authorizedFetch(`${API_BASE_URL}/v2/admin/gemini-caches${q ? `?${q}` : ''}`);
    },

    async getGeminiCacheMetrics(params = {}) {
        const p = new URLSearchParams();
        if (params.periodHours) p.append('period_hours', params.periodHours);
        if (params.groupBy) p.append('group_by', params.groupBy);
        const q = p.toString();
        return authorizedFetch(`${API_BASE_URL}/v2/admin/gemini-caches/metrics${q ? `?${q}` : ''}`);
    },

    async getGeminiCacheDetail(cacheKey) {
        const q = new URLSearchParams({ cache_key: cacheKey }).toString();
        return authorizedFetch(`${API_BASE_URL}/v2/admin/gemini-caches/entry?${q}`);
    },

    async listGeminiCacheEvents(cacheKey, params = {}) {
        const p = new URLSearchParams({ cache_key: cacheKey });
        if (params.limit) p.append('limit', params.limit);
        return authorizedFetch(`${API_BASE_URL}/v2/admin/gemini-caches/entry/events?${p.toString()}`);
    },

    async invalidateGeminiCache(cacheKey) {
        const q = new URLSearchParams({ cache_key: cacheKey }).toString();
        return authorizedFetch(`${API_BASE_URL}/v2/admin/gemini-caches/entry?${q}`, { method: 'DELETE' });
    },

    async listReminders(params = {}) {
        const query = new URLSearchParams(params).toString();
        const url = `${API_BASE_URL}/v2/admin/system-reminders${query ? `?${query}` : ''}`;
        return authorizedFetch(url);
    },

    async getReminder(id) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/system-reminders/${encodeURIComponent(id)}`);
    },

    async createReminder(data) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/system-reminders`, {
            method: 'POST',
            body: data,
        });
    },

    async updateReminder(id, data) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/system-reminders/${encodeURIComponent(id)}`, {
            method: 'PUT',
            body: data,
        });
    },

    async deleteReminder(id, force = false) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/system-reminders/${encodeURIComponent(id)}${force ? '?force=true' : ''}`, {
            method: 'DELETE',
        });
    },

    async listReminderVariables() {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/system-reminders/variables`);
    },

    async listReminderConditions() {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/system-reminders/conditions`);
    },

    async toggleReminder(id) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/system-reminders/${encodeURIComponent(id)}/toggle`, {
            method: 'POST',
        });
    },

    async previewReminder(id, mockState = {}) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/system-reminders/${encodeURIComponent(id)}/preview`, {
            method: 'POST',
            body: { mock_state: mockState },
        });
    },

    async getHubSession(userId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/hub/session/${encodeURIComponent(userId)}`);
    },

    async pushHubEvent(data) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/hub/events`, {
            method: 'POST',
            body: data,
        });
    },

    async pushHubEventsBatch(events) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/hub/events/batch`, {
            method: 'POST',
            body: { events },
        });
    },

    async sandboxHealth() {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/sandbox/health`);
    },

    async listSandboxes({ state, page = 1, pageSize = 20 } = {}) {
        const params = new URLSearchParams();
        if (state) params.append('state', state);
        params.append('page', page);
        params.append('page_size', pageSize);
        const query = params.toString();
        return authorizedFetch(`${API_BASE_URL}/v2/admin/sandbox/sandboxes?${query}`);
    },

    async getSandbox(sandboxId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/sandbox/sandboxes/${sandboxId}`);
    },

    async killSandbox(sandboxId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/sandbox/sandboxes/${sandboxId}`, {
            method: 'DELETE',
        });
    },

    async pauseSandbox(sandboxId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/sandbox/sandboxes/${sandboxId}/pause`, {
            method: 'POST',
        });
    },

    async resumeSandbox(sandboxId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/sandbox/sandboxes/${sandboxId}/resume`, {
            method: 'POST',
        });
    },

    async renewSandbox(sandboxId, minutes = 30) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/sandbox/sandboxes/${sandboxId}/renew?minutes=${minutes}`, {
            method: 'POST',
        });
    },

    async createTestSandbox(image, timeoutMinutes = 10) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/sandbox/test/create`, {
            method: 'POST',
            body: { image, timeout_minutes: timeoutMinutes },
        });
    },

    async execSandboxCommand(sandboxId, command) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/sandbox/test/${sandboxId}/exec`, {
            method: 'POST',
            body: { command },
        });
    },

    async listSandboxFiles(sandboxId, path = '/') {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/sandbox/test/${sandboxId}/files?path=${encodeURIComponent(path)}`);
    },

    async viewSandboxFile(sandboxId, path) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/sandbox/test/${sandboxId}/files/view?path=${encodeURIComponent(path)}`);
    },

    sandboxFileDownloadUrl(sandboxId, path) {
        return `${API_BASE_URL}/v2/admin/sandbox/test/${sandboxId}/files/download?path=${encodeURIComponent(path)}`;
    },

    async crawlerExtract(url, extractTextOnly = true) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/crawler/extract`, {
            method: 'POST',
            body: { url, extract_text_only: extractTextOnly },
        });
    },

    async listSignalOutputs({ ticker, event_type, event_date, source, signal_status, limit = 50, offset = 0 } = {}) {
        const params = new URLSearchParams();
        if (ticker) params.set('ticker', ticker);
        if (event_type) params.set('event_type', event_type);
        if (event_date) params.set('event_date', event_date);
        if (source) params.set('source', source);
        if (signal_status) params.set('signal_status', signal_status);
        params.set('limit', limit);
        params.set('offset', offset);
        return authorizedFetch(`${API_BASE_URL}/v2/admin/signal-outputs/?${params}`);
    },
    async getSignalOutput(outputId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/signal-outputs/${outputId}`);
    },
    async getSignalOutputStats() {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/signal-outputs/stats`);
    },
    async deleteSignalOutput(outputId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/signal-outputs/${outputId}`, { method: 'DELETE' });
    },

    async listScheduledTasks() {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/scheduled-tasks/`);
    },
    async getScheduledTask(taskId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/scheduled-tasks/${taskId}`);
    },
    async createScheduledTask(data) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/scheduled-tasks/`, {
            method: 'POST',
            body: data,
        });
    },
    async updateScheduledTask(taskId, data) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/scheduled-tasks/${taskId}`, {
            method: 'PUT',
            body: data,
        });
    },
    async deleteScheduledTask(taskId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/scheduled-tasks/${taskId}`, {
            method: 'DELETE',
        });
    },
    async runScheduledTask(taskId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/scheduled-tasks/${taskId}/run`, {
            method: 'POST',
        });
    },
    async cancelScheduledTask(taskId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/scheduled-tasks/${taskId}/cancel`, {
            method: 'POST',
        });
    },
    async cancelScheduledTaskRun(taskId, runId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/scheduled-tasks/${taskId}/runs/${runId}/cancel`, {
            method: 'POST',
        });
    },
    async listScheduledTaskRuns(taskId, limit = 20) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/scheduled-tasks/${taskId}/runs?limit=${limit}`);
    },
    async listScheduledTaskOutputTypes() {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/scheduled-tasks/output-types`);
    },
    async previewScheduledTaskOutputs(payload) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/scheduled-tasks/preview-outputs`, {
            method: 'POST',
            body: payload,
        });
    },
    async previewScheduledTaskInput(payload) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/scheduled-tasks/preview-input`, {
            method: 'POST',
            body: payload,
        });
    },
    async listWebhookPresets() {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/task-outputs/webhook-presets`);
    },

    async listInsightCategories(includeInactive = false) {
        const qs = includeInactive ? '?include_inactive=true' : '';
        return authorizedFetch(`${API_BASE_URL}/v2/admin/insight-categories/${qs}`);
    },
    async getInsightCategory(code) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/insight-categories/${encodeURIComponent(code)}`);
    },
    async createInsightCategory(payload) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/insight-categories/`, {
            method: 'POST',
            body: payload,
        });
    },
    async updateInsightCategory(code, payload) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/insight-categories/${encodeURIComponent(code)}`, {
            method: 'PUT',
            body: payload,
        });
    },
    async deactivateInsightCategory(code) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/insight-categories/${encodeURIComponent(code)}`, {
            method: 'DELETE',
        });
    },

    async listFeedVisibilityRules({ limit = 50, offset = 0 } = {}) {
        const qs = new URLSearchParams({ limit: String(limit), offset: String(offset) });
        return authorizedFetch(`${API_BASE_URL}/v2/admin/feed-visibility/?${qs.toString()}`);
    },
    async getActiveFeedVisibilityRule() {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/feed-visibility/active`);
    },
    async getFeedVisibilityRule(ruleId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/feed-visibility/${encodeURIComponent(ruleId)}`);
    },
    async createFeedVisibilityRule(payload) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/feed-visibility/`, {
            method: 'POST',
            body: payload,
        });
    },
    async updateFeedVisibilityRule(ruleId, payload) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/feed-visibility/${encodeURIComponent(ruleId)}`, {
            method: 'PUT',
            body: payload,
        });
    },
    async activateFeedVisibilityRule(ruleId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/feed-visibility/${encodeURIComponent(ruleId)}/activate`, {
            method: 'POST',
        });
    },
    async previewFeedVisibilityRule(predicate, sampleLimit = 10) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/feed-visibility/preview`, {
            method: 'POST',
            body: { predicate, sample_limit: sampleLimit },
        });
    },
    async listFeedVisibilityAudit(ruleId, { limit = 50, offset = 0 } = {}) {
        const qs = new URLSearchParams({ limit: String(limit), offset: String(offset) });
        return authorizedFetch(
            `${API_BASE_URL}/v2/admin/feed-visibility/${encodeURIComponent(ruleId)}/audit?${qs.toString()}`,
        );
    },
    async getFeedVisibilitySchema({ refresh = false } = {}) {
        const qs = refresh ? '?refresh=true' : '';
        return authorizedFetch(`${API_BASE_URL}/v2/admin/feed-visibility/schema${qs}`);
    },
    async invalidateFeedVisibilitySchema() {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/feed-visibility/schema/invalidate`, {
            method: 'POST',
        });
    },

    async listInstrumentsWithInsights({ asset_class, category, query, offset = 0, limit = 20 } = {}) {
        const qs = new URLSearchParams();
        if (asset_class) qs.set('asset_class', asset_class);
        if (category) qs.set('category', category);
        if (query) qs.set('query', query);
        qs.set('offset', String(offset));
        qs.set('limit', String(limit));
        return authorizedFetch(`${API_BASE_URL}/v2/admin/instrument-insights?${qs.toString()}`);
    },
    async getInstrumentInsightDetail(insightId, { presignedTtlSeconds } = {}) {
        const qs = presignedTtlSeconds ? `?presigned_ttl_seconds=${presignedTtlSeconds}` : '';
        return authorizedFetch(
            `${API_BASE_URL}/v2/admin/instrument-insights/id/${encodeURIComponent(insightId)}${qs}`,
        );
    },
    async getInstrumentInsightTranscript(insightId, { inline = false, presignedTtlSeconds } = {}) {
        const qs = new URLSearchParams();
        if (inline) qs.set('inline', 'true');
        if (presignedTtlSeconds) qs.set('presigned_ttl_seconds', String(presignedTtlSeconds));
        const q = qs.toString();
        return authorizedFetch(
            `${API_BASE_URL}/v2/admin/instrument-insights/id/${encodeURIComponent(insightId)}/transcript${q ? `?${q}` : ''}`,
        );
    },
    async getInstrumentInsightStats(insightId, { topViewersLimit, dailyWindowDays } = {}) {
        const qs = new URLSearchParams();
        if (topViewersLimit) qs.set('top_viewers_limit', String(topViewersLimit));
        if (dailyWindowDays) qs.set('daily_window_days', String(dailyWindowDays));
        const q = qs.toString();
        return authorizedFetch(
            `${API_BASE_URL}/v2/admin/instrument-insights/id/${encodeURIComponent(insightId)}/stats${q ? `?${q}` : ''}`,
        );
    },
    async ingestInstrumentInsight(payload) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/instrument-insights/ingest`, {
            method: 'POST',
            body: payload,
        });
    },
    async reindexInstrumentInsight(insightId, { targets = ['s3', 'rag'] } = {}) {
        return authorizedFetch(
            `${API_BASE_URL}/v2/admin/instrument-insights/${encodeURIComponent(insightId)}/reindex`,
            { method: 'POST', body: { targets } },
        );
    },
    async deleteInstrumentInsight(insightId) {
        return authorizedFetch(
            `${API_BASE_URL}/v2/admin/instrument-insights/id/${encodeURIComponent(insightId)}`,
            { method: 'DELETE' },
        );
    },
    async updateInstrumentInsightStatus(insightId, { status, reviewNote } = {}) {
        return authorizedFetch(
            `${API_BASE_URL}/v2/admin/instrument-insights/id/${encodeURIComponent(insightId)}/status`,
            {
                method: 'PATCH',
                body: { status, review_note: reviewNote || null },
            },
        );
    },

    async listInstrumentProfiles({ asset_class, query, limit = 100 } = {}) {
        const qs = new URLSearchParams();
        if (asset_class) qs.set('asset_class', asset_class);
        if (query) qs.set('query', query);
        if (limit) qs.set('limit', String(limit));
        const q = qs.toString();
        return authorizedFetch(`${API_BASE_URL}/v2/admin/instrument-profiles${q ? `?${q}` : ''}`);
    },
    async getInstrumentProfile(canonicalId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/instrument-profiles/${encodeURIComponent(canonicalId)}`);
    },
    async upsertInstrumentProfile(canonicalId, payload) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/instrument-profiles/${encodeURIComponent(canonicalId)}`, {
            method: 'PUT',
            body: payload,
        });
    },

    // ─── Credentials (provider-scoped opaque secrets) ────────────────────────
    async listCredentials({ provider } = {}) {
        const qs = provider ? `?provider=${encodeURIComponent(provider)}` : '';
        return authorizedFetch(`${API_BASE_URL}/v2/admin/credentials${qs}`);
    },

    async getCredential(credentialId, { revealSecrets = false } = {}) {
        const qs = revealSecrets ? '?reveal_secrets=true' : '';
        return authorizedFetch(`${API_BASE_URL}/v2/admin/credentials/${encodeURIComponent(credentialId)}${qs}`);
    },

    async createCredential(payload) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/credentials`, { method: 'POST', body: payload });
    },

    async updateCredential(credentialId, payload) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/credentials/${encodeURIComponent(credentialId)}`, {
            method: 'PUT', body: payload,
        });
    },

    async deleteCredential(credentialId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/credentials/${encodeURIComponent(credentialId)}`, {
            method: 'DELETE',
        });
    },

    // ─── Sync configs (provider-agnostic resource sync) ──────────────────────
    async listSyncConfigs({ provider, resourceType, credentialId, includeInactive = false } = {}) {
        const params = new URLSearchParams();
        if (provider) params.append('provider', provider);
        if (resourceType) params.append('resource_type', resourceType);
        if (credentialId) params.append('credential_id', credentialId);
        if (includeInactive) params.append('include_inactive', 'true');
        const query = params.toString();
        return authorizedFetch(`${API_BASE_URL}/v2/admin/sync-configs${query ? `?${query}` : ''}`);
    },

    async getSyncConfig(configId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/sync-configs/${encodeURIComponent(configId)}`);
    },

    async createSyncConfig(payload) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/sync-configs`, { method: 'POST', body: payload });
    },

    async updateSyncConfig(configId, payload) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/sync-configs/${encodeURIComponent(configId)}`, {
            method: 'PUT', body: payload,
        });
    },

    async deleteSyncConfig(configId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/sync-configs/${encodeURIComponent(configId)}`, {
            method: 'DELETE',
        });
    },

    async previewSync(configId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/sync-configs/${encodeURIComponent(configId)}/preview`, {
            method: 'POST',
        });
    },

    async triggerSync(configId, payload = {}) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/sync-configs/${encodeURIComponent(configId)}/sync`, {
            method: 'POST', body: payload,
        });
    },

    async listEnvironments(includeInactive = false) {
        const qs = includeInactive ? '?include_inactive=true' : '';
        return authorizedFetch(`${API_BASE_URL}/v2/admin/environments${qs}`);
    },

    async getEnvironment(envId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/environments/${encodeURIComponent(envId)}`);
    },

    async createEnvironment(payload) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/environments`, { method: 'POST', body: payload });
    },

    async updateEnvironment(envId, payload) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/environments/${encodeURIComponent(envId)}`, {
            method: 'PUT',
            body: payload,
        });
    },

    async setDefaultEnvironment(envId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/environments/${encodeURIComponent(envId)}/set-default`, {
            method: 'POST',
        });
    },

    async deleteEnvironment(envId) {
        return authorizedFetch(`${API_BASE_URL}/v2/admin/environments/${encodeURIComponent(envId)}`, { method: 'DELETE' });
    },
};

export const getHubPendingCount = async () => {
    const res = await authorizedFetch(`${API_BASE_URL}/v2/hub/session`);
    return res?.pending_count || 0;
};

// Check if current user has admin role
export const isAdmin = () => {
    const token = getAccessToken();
    if (!token) return false;
    try {
        const payload = decodeJwtPayload(token);
        const roles = payload?.auth || '';
        return roles.includes('ROLE_ADMIN') || roles.includes('ROLE_EMPLOYEE');
    } catch {
        return false;
    }
};

export const fetchNotifications = async (appCode = 'nebula', page = 0, size = 20, groupType = '') => {
    const params = new URLSearchParams({ page, size });
    if (groupType) params.set('groupType', groupType);
    const url = `${AUTH_BASE_URL}/api/notification/v2/${appCode}/message/list?${params}`;
    return authorizedFetch(url);
};

export const fetchNotificationCount = async (appCode = 'nebula') => {
    const url = `${AUTH_BASE_URL}/api/notification/v2/${appCode}/message/count`;
    return authorizedFetch(url);
};

export const markNotificationRead = async (appCode = 'nebula', notificationId) => {
    const url = `${AUTH_BASE_URL}/api/notification/v2/${appCode}/message/update-read/${notificationId}`;
    return authorizedFetch(url, { method: 'POST' });
};

export const markAllNotificationsRead = async (appCode = 'nebula') => {
    const url = `${AUTH_BASE_URL}/api/notification/v2/${appCode}/message/update-read-all`;
    return authorizedFetch(url, { method: 'POST' });
};

export const markAllNotificationsViewed = async (appCode = 'nebula') => {
    const url = `${AUTH_BASE_URL}/api/notification/v2/${appCode}/message/update-view-all`;
    return authorizedFetch(url, { method: 'POST' });
};

export const instrumentApi = {
    async getFeed({
        q,
        symbol,
        canonical_id,
        asset_class,
        category,
        sector,
        period_days,
        sort,
        cursor,
        limit = 50,
    } = {}) {
        const qs = new URLSearchParams();
        if (q) qs.set('q', q);
        if (symbol) qs.set('symbol', Array.isArray(symbol) ? symbol.join(',') : symbol);
        if (canonical_id) qs.set('canonical_id', canonical_id);
        if (asset_class) qs.set('asset_class', asset_class);
        if (category) qs.set('category', category);
        if (sector) qs.set('sector', sector);
        if (period_days) qs.set('period_days', String(period_days));
        if (sort) qs.set('sort', sort);
        if (cursor) qs.set('cursor', cursor);
        if (limit) qs.set('limit', String(limit));
        const query = qs.toString();
        return authorizedFetch(`${API_BASE_URL}/v2/instruments/insights/feed${query ? `?${query}` : ''}`);
    },
    async getInsightDetail(insightId) {
        return authorizedFetch(
            `${API_BASE_URL}/v2/instruments/insights/detail/${encodeURIComponent(insightId)}`,
        );
    },
    async suggest({ q, symbol_limit, insight_limit } = {}) {
        const qs = new URLSearchParams();
        if (q) qs.set('q', q);
        if (symbol_limit) qs.set('symbol_limit', String(symbol_limit));
        if (insight_limit !== undefined) qs.set('insight_limit', String(insight_limit));
        return authorizedFetch(`${API_BASE_URL}/v2/instruments/suggest?${qs.toString()}`);
    },
    async listInsightCategoriesPublic() {
        return authorizedFetch(`${API_BASE_URL}/v2/instruments/insight-categories`);
    },
    async trackInsightEvent(insightId, { event_type = 'view', session_id, meta } = {}) {
        return authorizedFetch(
            `${API_BASE_URL}/v2/instruments/insights/${encodeURIComponent(insightId)}/track`,
            {
                method: 'POST',
                body: { event_type, session_id, meta },
            },
        );
    },
};
